// Home page - displays upcoming events and a table of all past events
// Users can filter events by type and sort the table by different columns

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { app } from './firebase-config';
import { FiDownload } from 'react-icons/fi';
import * as XLSX from 'xlsx'; // Library for creating Excel files
import { saveAs } from 'file-saver'; // Function to save files to the user's computer
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useAuthUser } from './blaturData';
import { typeInfo } from './eventTypes';
import { isUpcoming, quarterStart } from './upcoming';

const normalize = (text) => (text || '').trim().toLowerCase();

// Combines the hardcoded history with events from the database (which require login).
// A database event replaces the hardcoded row with the same name and quarter, or the only row
// with the same type and quarter. Database events without a match are added.
// Upcoming events (see upcoming.js) are left out here; they are listed above the completed ones (upcomingRows).
const mergeEvents = (staticRows, dbEvents) => {
  const rows = staticRows.map((row, i) => ({ ...row, id: `static-${i}` }));
  const replaced = new Set();
  dbEvents.filter(e => !isUpcoming(e)).forEach(e => {
    const row = { id: e.id, event: e.sted_konsept, type: e.event_type, vert: (e.Vert || []).join(' & '), kvartal: e.Kvartal };
    const free = rows.map((r, i) => i).filter(i => !replaced.has(i));
    let index = free.find(i => normalize(rows[i].event) === normalize(e.sted_konsept) && rows[i].kvartal === e.Kvartal);
    if (index === undefined) {
      const sameSlot = free.filter(i => rows[i].type === e.event_type && rows[i].kvartal === e.Kvartal);
      if (sameSlot.length === 1) index = sameSlot[0];
    }
    if (index === undefined) {
      rows.push(row);
      replaced.add(rows.length - 1);
    } else {
      rows[index] = row;
      replaced.add(index);
    }
  });
  return rows;
};

// "2026-11-28" -> "28. november 2026"
const formatDate = (isoDate) => new Date(isoDate).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' });

// Upcoming events from the database, latest first (like the quarter sorting below). The date takes the place of
// sted/konsept, which usually isn't decided yet; if it is, the date is shown after it.
const upcomingRows = (dbEvents) => dbEvents
  .filter(e => isUpcoming(e))
  .map(e => ({
    id: e.id,
    when: e.date ? new Date(e.date) : quarterStart(e.Kvartal),
    event: [(e.sted_konsept || '').trim(), e.date ? formatDate(e.date) : 'Dato ikke satt'].filter(Boolean).join(' · '),
    type: e.event_type,
    vert: (e.Vert || []).join(' & '),
    kvartal: e.Kvartal,
  }))
  .sort((a, b) => b.when - a.when);

// Event type as a coloured pill with the emoji first, e.g. "KL🥩" -> 🥩 KL
const TypePill = ({ type }) => {
  const { emoji, label, color } = typeInfo(type);
  return (
    <span className="type-pill" style={{ '--cat': color }}>
      <span aria-hidden="true">{emoji}</span>{label}
    </span>
  );
};

function Home() {
  const [userMap, setUserMap] = useState({});
  const [dbEvents, setDbEvents] = useState([]);
  const user = useAuthUser();

  useEffect(() => {
    if (!user) return;
    getDocs(collection(getFirestore(app), 'events'))
      .then(snapshot => setDbEvents(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(error => console.error('Error fetching events: ', error));
  }, [user]);

  useEffect(() => {
    const fetchUsers = async () => {
      const db = getFirestore(app);
      const snapshot = await getDocs(collection(db, 'users'));
      const map = {};
      snapshot.docs.forEach(d => {
        const name = d.data().name;
        if (name) map[name.toLowerCase()] = d.id;
      });
      setUserMap(map);
    };
    fetchUsers();
  }, []);

  const renderLinkedNames = (text) => {
    const parts = text.split(/\s*&\s*|,\s*/);
    return parts.map((name, i) => {
      const trimmed = name.trim();
      const userId = userMap[trimmed.toLowerCase()];
      return (
        <React.Fragment key={i}>
          {i > 0 && ', '}
          {userId ? <Link to={`/profil/${userId}`} className="user-link">{trimmed}</Link> : trimmed}
        </React.Fragment>
      );
    });
  };

  // Event history before the database was used. Merged with database events below.
  // Each event has: name, type (emoji), organizer, and quarter/year
  const staticTableData = useMemo(() => [
    { event: 'Engebret Cafe', type: 'KL🥩', vert: 'Gil', kvartal: '2026 Q4' },
    { event: 'Italia-Toscana', type: 'Blåtur🌍', vert: 'Thomas & Matias', kvartal: '2026 Q3' },
    { event: 'Yum Cha', type: 'KL🥩', vert: 'Martin', kvartal: '2026 Q2' },
    { event: 'Torres', type: 'Vino🍷', vert: 'Anders Ø, Sigbjørn', kvartal: '2026 Q1' },
    { event: 'Guttas Fitnessmåling #3', type: 'Fitness💪', vert: 'Nils & Gil', kvartal: '2026 Q1' },
    { event: 'Little Wolf', type: 'KL🥩', vert: 'Nikolai', kvartal: '2025 Q4' },
    { event: 'California', type: 'Vino🍷', vert: 'Anders S', kvartal: '2025 Q4' },
    { event: 'Belgia-Brussel', type: 'Blåtur🌍', vert: 'Audun & Lasse', kvartal: '2025 Q3' },
    { event: 'Guttas Fitnessmåling #2', type: 'Fitness💪', vert: 'Nils & Gil', kvartal: '2025 Q2' },
    { event: 'Naturvin', type: 'Vino🍷', vert: 'Anders L', kvartal: '2025 Q2'},
    { event: 'Jewel of India', type: 'KL🥩', vert: 'Diderik', kvartal: '2025 Q1'},
    { event: 'Guttas Fitnessmåling', type: 'Fitness💪', vert: 'Nils & Gil', kvartal: '2025 Q1' },
    { event: 'Julebord hos Diderik', type: 'Julebord🎄', vert: 'Diderik', kvartal: '2024 Q4' },
    { event: 'Txotx', type: 'KL🥩', vert: 'Markus', kvartal: '2024 Q3' },
    { event: 'Båttur', type: 'KL🥩', vert: 'Arne', kvartal: '2023 Q2' },
    { event: 'Televerket', type: 'KL🥩', vert: 'Matias', kvartal: '2023 Q1' },
    { event: 'Kverneriet', type: 'KL🥩', vert: 'Christian E', kvartal: '2022 Q4' },
    { event: 'Köd', type: 'KL🥩', vert: 'Magnus', kvartal: '2020 Q3' },
    { event: 'The Night Pig', type: 'KL🥩', vert: 'Gustaf', kvartal: '2019 Q4' },
    { event: 'Ahan', type: 'KL🥩', vert: 'Audun', kvartal: '2019 Q2' },
    { event: 'Hot Shop', type: 'KL🥩', vert: 'Njaal', kvartal: '2019 Q1' },
    { event: 'Kontrast', type: 'KL🥩', vert: 'Petter', kvartal: '2018 Q4' },
    { event: 'Wallmanns', type: 'KL🥩', vert: 'Nikolai', kvartal: '2018 Q3' },
    { event: 'Kullt', type: 'KL🥩', vert: 'Glenn', kvartal: '2018 Q2' },
    { event: 'Golden Chimp', type: 'KL🥩', vert: 'Håvard', kvartal: '2018 Q1' },
    { event: 'Festningen ', type: 'KL🥩', vert: 'Martin', kvartal: '2017 Q4' },
    { event: 'Grisen ', type: 'KL🥩', vert: 'Gil', kvartal: '2017 Q3' },
    { event: 'Der Peppern Gror', type: 'KL🥩', vert: 'Adrian', kvartal: '2017 Q2' },
    { event: 'Smia Galleri', type: 'KL🥩', vert: 'Christian B', kvartal: '2017 Q1' },
    { event: 'Piemonte', type: 'Vino🍷', vert: 'Magnus', kvartal: '2022 Q4' },
    { event: 'Spania', type: 'Vino🍷', vert: 'Markus', kvartal: '2020 Q3' },
    { event: 'Piemonte', type: 'Vino🍷', vert: 'Håvard', kvartal: '2020 Q1' },
    { event: 'Bobler', type: 'Vino🍷', vert: 'Gustaf', kvartal: '2019 Q3' },
    { event: 'Rosé fra Provence', type: 'Vino🍷', vert: 'Adrian', kvartal: '2019 Q1' },
    { event: 'Njaals vinreise', type: 'Vino🍷', vert: 'Njaal', kvartal: '2018 Q4' },
    { event: 'Rødvin', type: 'Vino🍷', vert: 'Arne', kvartal: '2018 Q3' },
    { event: 'Chenin Blanc', type: 'Vino🍷', vert: 'Matias', kvartal: '2018 Q2' },
    { event: 'Zinfandel', type: 'Vino🍷', vert: 'Gil', kvartal: '2018 Q1' },
    { event: 'Kroatia-Pula', type: 'Blåtur🌍', vert: 'Njaal & Magnus', kvartal: '2017 Q3' },
    { event: 'Tyskland-Munchen', type: 'Blåtur🌍', vert: 'Christian E & Matias', kvartal: '2018 Q4' },
    { event: 'Polen-Warszawa', type: 'Blåtur🌍', vert: 'Petter & Martin', kvartal: '2019 Q3' },
    { event: 'Norge-Veggli', type: 'Blåtur🌍', vert: 'Njaal & Martin', kvartal: '2020 Q4' },
    { event: 'Tyskland-Berlin', type: 'Blåtur🌍', vert: 'Gil, Magnus & Nikolai', kvartal: '2023 Q4' },
    { event: 'Norge-Lenningen', type: 'Blåtur🌍', vert: 'Christian B & Anders S', kvartal: '2021 Q4' },
    { event: 'Ciber & EU', type: 'KD🧠', vert: 'Njaal & Markus', kvartal: '2018 Q2' },
    { event: 'Fra billig til dyr vin', type: 'Vino🍷', vert: 'Gustaf', kvartal: '2024 Q2' },
    { event: 'Tine & Tom Wilhelmsen', type: 'KD🧠', vert: 'Adrian & Gil', kvartal: '2019 Q1' },
    { event: 'Intility', type: 'KD🧠', vert: 'Håvard', kvartal: '2020 Q1' },
    { event: 'HitecVision & Celsio', type: 'KD🧠', vert: 'Petter & Markus', kvartal: '2023 Q1' },
    { event: 'Pythonkurs', type: 'KD🧠', vert: 'Magnus', kvartal: '2021 Q1' },
    { event: 'Sognsvann', type: 'Julegrøt🎅', vert: 'Martin', kvartal: '2023 Q4' },
    { event: 'Hos Anders S', type: 'Julegrøt🎅', vert: 'Anders S', kvartal: '2022 Q4' },
    { event: 'Hos Petter', type: 'Julegrøt🎅', vert: 'Petter', kvartal: '2021 Q4' },
    { event: 'Sognsvann', type: 'Julegrøt🎅', vert: 'Martin&Petter', kvartal: '2020 Q4' },
    { event: 'Hos Martin', type: 'Julegrøt🎅', vert: 'Martin', kvartal: '2019 Q4' },
    { event: 'Hos Martin', type: 'Julegrøt🎅', vert: 'Martin', kvartal: '2018 Q4' },
    { event: 'Hos Martin', type: 'Julegrøt🎅', vert: 'Martin', kvartal: '2017 Q4' },
    { event: 'Nikolai jobb', type: 'Julebord🎄', vert: 'Nikolai', kvartal: '2023 Q4' },
    { event: 'Hos Nikolai', type: 'Julebord🎄', vert: 'Nikolai', kvartal: '2019 Q4' },
    { event: 'Hos Glenn', type: 'Julebord🎄', vert: 'Glenn', kvartal: '2018 Q4' },
    { event: 'Hos Nikolai', type: 'Julebord🎄', vert: 'Nikolai', kvartal: '2017 Q4' },
    // Add more items as needed
  ], []); // Empty dependency array means this only runs once on mount

  const tableData = useMemo(() => mergeEvents(staticTableData, dbEvents), [staticTableData, dbEvents]);
  // Shown on top of the list whatever the sorting, but not in the chart or the Excel export
  const upcoming = useMemo(() => upcomingRows(dbEvents), [dbEvents]);

  // State to store the current sort configuration (which column and direction)
  const [sortConfig, setSortConfig] = useState({ key: 'kvartal', direction: 'descending' });
  // State to store which event type to filter by (e.g., 'KL🥩', 'Vino🍷')
  const [filter, setFilter] = useState(null);
  // State to track which filter button is highlighted as "active" 
  const [activeFilter, setActiveFilter] = useState('');
  // State to toggle between list view and chart view
  const [viewMode, setViewMode] = useState('list');
  // State to toggle chart grouping: 'year' or 'participant'
  const [chartMode, setChartMode] = useState('year');

  // Function to request sorting by clicking a column header
  // Toggles between ascending and descending for the same column
  const requestSort = (key) => {
    let direction = 'ascending'; // Default to ascending
    // If clicking the same column, toggle direction
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Function to filter the table by event type
  const applyFilter = (itemName) => {
    setFilter(itemName); // Filter the events by this type
    setActiveFilter(itemName); // Highlight the active filter button
  };
 // Function to clear all filters and show all events
  const clearFilter = () => {
    setFilter(null); // Remove the filter
    setActiveFilter(''); // Clear the active filter highlight
  };

  // Function to export the currently visible table data to an Excel file
  const exportToExcel = () => {
  // Create worksheet from your current visible data
  const ws = XLSX.utils.json_to_sheet(sortedAndFilteredItems.map(({ id, ...item }) => item));

  // Create workbook and add the worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Events"); // Add the data to a sheet named "Events"

  // Generate Excel file and trigger download
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], { type: "application/octet-stream" });
  saveAs(blob, "event_history.xlsx"); // Save the file as "event_history.xlsx"
};

  // useMemo hook efficiently sorts and filters items without recalculating every render
  // This improves performance for large lists
  const sortedAndFilteredItems = useMemo(() => {
    let items = [...tableData]; // Make a copy to avoid changing the original data
    // Filter items by type if a filter is selected
    if (filter) {
      items = items.filter(item => item.type === filter);
    }
    // Sort items based on the current sort configuration
    if (sortConfig.key !== null) {
      items.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return items; // Return the sorted and filtered items
  }, [tableData, sortConfig, filter]); // Recalculate only if these values change

  // Chart colors
  const CHART_COLORS = ['#2d6a8a', '#3a87a8', '#4aa3c4', '#62b8d6', '#7dcde6', '#3577a0', '#5098b8', '#68b0cc', '#8ac4da', '#a2d8ea'];

  // Data for "By Year" chart
  const chartByYear = useMemo(() => {
    const counts = {};
    const items = filter ? tableData.filter(item => item.type === filter) : tableData;
    items.forEach(item => {
      const year = item.kvartal.split(' ')[0];
      counts[year] = (counts[year] || 0) + 1;
    });
    const years = Object.keys(counts).map(Number);
    const result = [];
    const lastYear = Math.max(new Date().getFullYear(), ...years);
    for (let y = 2017; y <= lastYear; y++) {
      result.push({ name: String(y), count: counts[y] || 0 });
    }
    return result;
  }, [tableData, filter]);

  // Data for "By Participant" chart
  const chartByParticipant = useMemo(() => {
    const counts = {};
    const items = filter ? tableData.filter(item => item.type === filter) : tableData;
    items.forEach(item => {
      const names = item.vert.split(/\s*&\s*|,\s*/);
      names.forEach(n => {
        const trimmed = n.trim();
        if (trimmed) counts[trimmed] = (counts[trimmed] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [tableData, filter]);

  const chartData = chartMode === 'year' ? chartByYear : chartByParticipant;

  return (
    <div className="home-page">
    {/* Title for past events section */}
    <div className="events-heading-row">
      <h1 className="events_headliner">Alle eventer:</h1>
    </div>

        {/* View mode toggle */}
        <div className="button-container">
          <button
            className={`button-17${viewMode === 'list' ? '' : ' muted'}`}
            onClick={() => setViewMode('list')}
          >Listevisning</button>
          <button
            className={`button-17${viewMode === 'chart' ? '' : ' muted'}`}
            onClick={() => setViewMode('chart')}
          >Diagramvisning</button>
        </div>

        {/* Filter chips — horizontally scrollable on mobile */}
        <div className="filter-chip-bar">
          <button onClick={clearFilter}
            className={`filter-chip${activeFilter === '' ? ' active' : ''}`}
          >Alle</button>
          <button onClick={() => applyFilter('KL🥩')}
            className={`filter-chip${activeFilter === 'KL🥩' ? ' active' : ''}`}
          >🥩 KL</button>
          <button onClick={() => applyFilter('Vino🍷')}
            className={`filter-chip${activeFilter === 'Vino🍷' ? ' active' : ''}`}
          >🍷 Vino</button>
          <button onClick={() => applyFilter('Blåtur🌍')}
            className={`filter-chip${activeFilter === 'Blåtur🌍' ? ' active' : ''}`}
          >🌍 Blåtur</button>
          <button onClick={() => applyFilter('KD🧠')}
            className={`filter-chip${activeFilter === 'KD🧠' ? ' active' : ''}`}
          >🧠 KD</button>
          <button onClick={() => applyFilter('Julegrøt🎅')}
            className={`filter-chip${activeFilter === 'Julegrøt🎅' ? ' active' : ''}`}
          >🎅 Julegrøt</button>
          <button onClick={() => applyFilter('Fitness💪')}
            className={`filter-chip${activeFilter === 'Fitness💪' ? ' active' : ''}`}
          >💪 Fitness</button>
          <button onClick={() => applyFilter('Julebord🎄')}
            className={`filter-chip${activeFilter === 'Julebord🎄' ? ' active' : ''}`}
          >🎄 Julebord</button>
        </div>

      {viewMode === 'list' ? (
        <>
          {/* Table showing all past events */}
          <div className="table-export-row">
            <button className="export-icon-button" onClick={exportToExcel} title="Last ned Excel"><FiDownload /></button>
          </div>
          <div className="table-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => requestSort('event')}>
                  Sted/konsept {sortConfig.key === 'event' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}  
                </th>
                <th onClick={() => requestSort('type')}>
                  Event type {sortConfig.key === 'type' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                </th>
                <th onClick={() => requestSort('vert')}>
                  Vert {sortConfig.key === 'vert' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                </th>
                <th onClick={() => requestSort('kvartal')}>
                  Kvartal {sortConfig.key === 'kvartal' ? (sortConfig.direction === 'descending' ? '▼' : '▲') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {upcoming.filter(item => !filter || item.type === filter).map(item => (
                <tr key={item.id} className="upcoming-row">
                  <td className="upcoming-date">{item.event}</td>
                  <td><TypePill type={item.type} /></td>
                  <td>{renderLinkedNames(item.vert)}</td>
                  <td>{item.kvartal}</td>
                </tr>
              ))}
              {sortedAndFilteredItems.map(item => (
                <tr key={item.id}>
                  <td>{item.event}</td>
                  <td><TypePill type={item.type} /></td>
                  <td>{renderLinkedNames(item.vert)}</td>
                  <td>{item.kvartal}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      ) : (
        <div className="chart-view-container">
          <div className="chart-mode-toggle">
            <button
              className={`filter-chip${chartMode === 'year' ? ' active' : ''}`}
              onClick={() => setChartMode('year')}
            >Per år</button>
            <button
              className={`filter-chip${chartMode === 'participant' ? ' active' : ''}`}
              onClick={() => setChartMode('participant')}
            >Per arrangør</button>
          </div>
          <div className="chart-wrapper" style={{ width: '100%', height: Math.max(300, chartData.length * 36) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" interval={0} width={chartMode === 'participant' ? 100 : 50} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
                  formatter={(value) => [value, 'Events']}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
                  {chartData.map((entry, index) => (
                    <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;